import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiClient } from '../../services/apiClient';
import {
  Tag,
  Select,
  Row,
  Col,
  Typography,
  Alert,
  Tooltip,
  Drawer,
  Descriptions,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PageHelp from '../../components/PageHelp';
import PageHeader from '../../components/PageHeader';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import SyncHistoryCard from '../../components/SyncHistoryCard';

const { Text } = Typography;
const { Option } = Select;

type SyncEntry = {
  id: string;
  source: string;
  status: string;
  records: number | null;
  inserted: number | null;
  updated: number | null;
  failed: number | null;
  file: string | null;
  error: string | null;
  details: unknown;
  duration: number | null;
  timestamp: string;
  completedAt: string | null;
};

type SyncHistoryResponse = {
  data: SyncEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

const sourceLabels: Record<string, string> = {
  'VRS API': 'Data SIM sync from KORE IoT API',
  'VRS CDR': 'Call data record file import',
  Giacom: 'Crew SIM data sync',
};

// Short user-facing labels for the Service tag. Keys are the backend
// `source` values (unchanged); only the displayed text differs. See
// KAN-48 (VRS -> Data) and KAN-49 (Giacom -> Crew).
const sourceDisplayNames: Record<string, string> = {
  'VRS API': 'Data API',
  'VRS CDR': 'Data CDR',
  Giacom: 'Crew',
};

function statusTag(status: string) {
  if (status === 'completed' || status === 'success') {
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        OK
      </Tag>
    );
  }
  if (status === 'failed' || status === 'error') {
    return (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        Failed
      </Tag>
    );
  }
  if (status === 'started') {
    return (
      <Tag color="blue" icon={<ClockCircleOutlined />}>
        Running
      </Tag>
    );
  }
  return <Tag icon={<ClockCircleOutlined />}>{status}</Tag>;
}

function describeEntry(r: SyncEntry): string {
  const parts: string[] = [];
  if (r.records != null && r.records > 0)
    parts.push(`${r.records.toLocaleString()} records processed`);
  if (r.inserted != null && r.inserted > 0) parts.push(`${r.inserted.toLocaleString()} new`);
  if (r.updated != null && r.updated > 0) parts.push(`${r.updated.toLocaleString()} updated`);
  if (r.failed != null && r.failed > 0) parts.push(`${r.failed} failed`);
  if (r.file) parts.push(r.file);
  if (parts.length === 0 && r.error) return r.error.slice(0, 80);
  if (parts.length === 0) return 'No details';
  return parts.join(' · ');
}

function SyncHistory() {
  const auth = useAuth();
  const [entries, setEntries] = useState<SyncEntry[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [drawerEntry, setDrawerEntry] = useState<SyncEntry | null>(null);

  const fetchData = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (sourceFilter) params.set('source', sourceFilter);
      if (statusFilter) params.set('status', statusFilter);
      const result = await ApiClient.get<SyncHistoryResponse>(
        `/admin/sync-history?${params.toString()}`
      );
      setEntries(result.data);
      setPagination({
        page: result.pagination.page,
        total: result.pagination.total,
        limit: result.pagination.limit,
      });
    } catch (err) {
      setError('Failed to load sync history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.authenticated && auth.token && !auth.isLoading) fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.authenticated, auth.token, auth.isLoading, sourceFilter, statusFilter]);

  const columns = [
    {
      title: 'When',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (t: string) => (
        <Tooltip title={new Date(t).toLocaleString()}>
          <span>
            {new Date(t).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Service',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: string) => (
        <Tooltip title={sourceLabels[source] || source}>
          <Tag
            color={
              source === 'VRS API'
                ? 'blue'
                : source === 'VRS CDR'
                  ? 'orange'
                  : source === 'Giacom'
                    ? 'purple'
                    : 'default'
            }
          >
            {sourceDisplayNames[source] || source}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Result',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: string) => statusTag(s),
    },
    {
      title: 'What happened',
      key: 'description',
      render: (_: unknown, r: SyncEntry) => {
        const desc = describeEntry(r);
        const hasError = r.failed != null && r.failed > 0;
        return hasError ? <Text type="danger">{desc}</Text> : <span>{desc}</span>;
      },
    },
    {
      title: '',
      key: 'action',
      width: 36,
      render: (_: unknown, r: SyncEntry) => (
        <InfoCircleOutlined
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => setDrawerEntry(r)}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Sync History"
        actions={
          <PageHelp
            title="Sync History Help"
            sections={[
              {
                title: 'What is this?',
                content:
                  'This page shows all automated data synchronizations that keep the portal up to date.\n\nThree services run automatically:\n- Data API: Syncs Data SIM card details every hour\n- Data CDR: Imports call/data usage records from files daily\n- Crew: Syncs Crew SIM phone data daily',
              },
              {
                title: 'Statuses',
                content:
                  'OK (green): Completed successfully\nFailed (red): Something went wrong — check the error\nRunning (blue): Currently in progress',
              },
              {
                title: 'Filters',
                content:
                  'Use the dropdowns to filter by service or status. Click the info icon on any row to see full details.',
              },
            ]}
          />
        }
      />

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={10} sm={5}>
          <Select
            placeholder="All services"
            value={sourceFilter}
            onChange={v => setSourceFilter(v)}
            allowClear
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="vrs_api">Data API</Option>
            <Option value="cdr">Data CDR</Option>
            <Option value="giacom">Crew</Option>
          </Select>
        </Col>
        <Col xs={10} sm={5}>
          <Select
            placeholder="All results"
            value={statusFilter}
            onChange={v => setStatusFilter(v)}
            allowClear
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="completed">OK</Option>
            <Option value="failed">Failed</Option>
            <Option value="started">Running</Option>
          </Select>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.page)} size="small">
            Refresh
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />
      )}

      <ResponsiveTableOrCards
        dataSource={entries}
        rowKey={record => record.id}
        columns={columns}
        loading={loading}
        breakpoint="xl"
        serverSidePagination={true}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}\u2013${range[1]} of ${total}`,
          onChange: (p: number) => fetchData(p),
        }}
        renderCard={entry => <SyncHistoryCard entry={entry} onInfoClick={e => setDrawerEntry(e)} />}
        emptyText="No sync history found"
      />

      <Drawer
        title="Sync Details"
        open={!!drawerEntry}
        onClose={() => setDrawerEntry(null)}
        width={480}
      >
        {drawerEntry && (
          <Descriptions
            column={1}
            bordered
            size="small"
            items={[
              {
                label: 'Service',
                children: `${sourceDisplayNames[drawerEntry.source] || drawerEntry.source} — ${sourceLabels[drawerEntry.source] || ''}`,
              },
              { label: 'Result', children: statusTag(drawerEntry.status) },
              { label: 'Started', children: new Date(drawerEntry.timestamp).toLocaleString() },
              {
                label: 'Completed',
                children: drawerEntry.completedAt
                  ? new Date(drawerEntry.completedAt).toLocaleString()
                  : '—',
              },
              {
                label: 'Duration',
                children: drawerEntry.duration != null ? `${drawerEntry.duration} seconds` : '—',
              },
              {
                label: 'Records processed',
                children: drawerEntry.records?.toLocaleString() ?? '—',
              },
              { label: 'New records', children: drawerEntry.inserted?.toLocaleString() ?? '—' },
              { label: 'Updated records', children: drawerEntry.updated?.toLocaleString() ?? '—' },
              { label: 'Failed records', children: drawerEntry.failed?.toLocaleString() ?? '—' },
              { label: 'File', children: drawerEntry.file || '—' },
              ...(drawerEntry.error
                ? [{ label: 'Error', children: <Text type="danger">{drawerEntry.error}</Text> }]
                : []),
              ...(drawerEntry.details
                ? [
                    {
                      label: 'Raw details',
                      children: (
                        <pre style={{ fontSize: 11, maxHeight: 300, overflow: 'auto', margin: 0 }}>
                          {typeof drawerEntry.details === 'string'
                            ? drawerEntry.details
                            : JSON.stringify(drawerEntry.details, null, 2)}
                        </pre>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Drawer>
    </>
  );
}

export default SyncHistory;
